import { deployEnvironment, ENVIRONMENT_LABEL, useClients } from '@features/clients';
import {
    Button,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    LoadingRows,
    Modal,
    WriteError,
} from '@shared/ui';
import { useEffect, useMemo, useState } from 'react';

import { useClientsPreview, useStartClientsOperation } from '../hooks/use-release-operation';

import { clientsEnvironmentView } from './clients-environment';
import { ClientSelector } from './ClientSelector';
import { confirmView } from './operation-confirm';
import { previewProgressLabel } from './preview-chunks';
import { explainReleaseWriteFailure } from './release-error-copy';
import { TurnOffConfirmFields } from './TurnOffConfirmFields';
import { matchesPhrase, turnOffAlert } from './typed-phrase';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Turn a release off for chosen clients (ops-csm), behind the typed phrase
 * (ACR-1598). "Check overrides" runs the chunked preview and shows the overwrite
 * count as a lower bound; it informs the operator and does not gate the confirm
 * (a failed preview must not block a deliberate turn-off). Mounted only while open.
 */
export function TurnOffClientsDialog({
    release,
    onClose,
}: {
    release: ReleaseComponent;
    onClose: () => void;
}) {
    const environment = clientsEnvironmentView(deployEnvironment());
    const clients = useClients(environment.target);
    const start = useStartClientsOperation(release.id);
    const preview = useClientsPreview(release.id);
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [text, setText] = useState('');
    const failure = start.error ? explainReleaseWriteFailure(start.error) : null;
    const options = useMemo(() => clients.data ?? [], [clients.data]);
    const members = release.members ?? [];
    const busy = start.isPending || preview.isPending;
    const view = confirmView({
        releaseName: release.name,
        enabled: false,
        scope: 'clients',
        clientCount: selected.length,
        flags: members.map((member) => ({
            key: member.key,
            name: member.name,
            archived: member.archived,
        })),
        environment: ENVIRONMENT_LABEL[deployEnvironment()],
        ...(preview.data
            ? {
                  overwrite: {
                      count: preview.data.overwriteCount,
                      incomplete: preview.data.incomplete,
                  },
              }
            : {}),
    });
    const alert = turnOffAlert({
        scope: 'clients',
        flagCount: members.length,
        clientCount: selected.length,
        salesforceFlags: members.filter((member) => member.type === 'salesforce'),
    });

    // A count for another selection is a wrong count.
    const { reset: resetPreview } = preview;
    useEffect(() => {
        resetPreview();
    }, [selected, resetPreview]);

    const submit = () => {
        if (!matchesPhrase(text) || selected.length === 0) return;
        start.mutate({ enabled: false, tenantIds: selected }, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!start.isPending}
            title={
                selected.length === 0 ? `Turn off ${release.name} for specific clients` : view.title
            }
            description={view.overrideNotice}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={submit}
                        disabled={busy || selected.length === 0 || !matchesPhrase(text)}
                    >
                        {start.isPending ? 'Starting…' : view.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-[13px]">
                <p className="font-medium text-content-strong">{environment.label}</p>
                {clients.isPending ? (
                    <LoadingRows label="Loading clients…" />
                ) : clients.isError ? (
                    <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
                ) : options.length === 0 ? (
                    <EmptyState title="No clients" description="This environment has no clients." />
                ) : (
                    <ClientSelector
                        clients={options}
                        selected={selected}
                        onChange={setSelected}
                        disabled={busy}
                    />
                )}
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                preview.mutate(selected);
                            }}
                            disabled={busy || selected.length === 0}
                        >
                            Check overrides
                        </Button>
                        {preview.isPending && preview.progress !== null && (
                            <span role="status" className="text-xs text-content-muted">
                                {previewProgressLabel(preview.progress)}
                            </span>
                        )}
                    </div>
                    {view.overwriteLabel !== undefined && (
                        <p className="text-xs text-content-body">{view.overwriteLabel}</p>
                    )}
                    {preview.isError && (
                        <p role="alert" className="text-xs text-danger-text">
                            The overrides could not be checked. You can still turn the release off.
                        </p>
                    )}
                </div>
                <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                        {view.summary}
                    </p>
                    <ul className="flex flex-col gap-1.5">
                        {view.flags.map((flag) => (
                            <li key={flag.key} className="flex items-center gap-2">
                                <FlagKeyPill>{flag.key}</FlagKeyPill>
                                {flag.note !== undefined && (
                                    <span className="text-xs text-content-muted">{flag.note}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                <TurnOffConfirmFields
                    sentence={alert.sentence}
                    salesforceNotes={alert.salesforceNotes}
                    text={text}
                    onChange={setText}
                    disabled={start.isPending}
                />
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    start.error && <WriteError error={start.error} />
                )}
            </div>
        </Modal>
    );
}
